import * as THREE from 'three';
import type { ParticleSystem } from './Particles';

/** A light or fire socket in world space (prop sockets, markers, portals). */
export interface LightSource {
  kind: 'fire' | 'light';
  pos: THREE.Vector3;
  color: THREE.Color;
  /** Authoring intensity (prop extras: 2.5–5). */
  intensity: number;
  /** Authoring range (m). */
  distance: number;
  /** Terrain height below the socket (light pool). */
  ground: number;
}

let poolTex: THREE.Texture | null = null;
function poolTexture(): THREE.Texture {
  if (poolTex) return poolTex;
  const s = 128;
  const c = document.createElement('canvas');
  c.width = c.height = s;
  const g = c.getContext('2d');
  if (!g) throw new Error('2D canvas unavailable');
  const grd = g.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
  grd.addColorStop(0, 'rgba(255,255,255,1)');
  grd.addColorStop(0.3, 'rgba(255,255,255,0.55)');
  grd.addColorStop(0.65, 'rgba(255,255,255,0.16)');
  grd.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = grd;
  g.fillRect(0, 0, s, s);
  poolTex = new THREE.CanvasTexture(c);
  poolTex.colorSpace = THREE.SRGBColorSpace;
  return poolTex;
}

const HALO_VERT = /* glsl */ `
attribute vec4 aHalo;    // centre xyz, size
attribute vec4 aColor;   // rgb, phase
uniform float uTime;
varying vec2 vUv;
varying vec3 vColor;
#include <fog_pars_vertex>
void main() {
  vUv = uv;
  float flick = 0.82 + 0.1 * sin(uTime * 13.0 + aColor.w * 7.0) + 0.08 * sin(uTime * 23.0 + aColor.w * 3.0);
  vColor = aColor.rgb * flick;
  vec4 mvPosition = viewMatrix * vec4(aHalo.xyz, 1.0);
  mvPosition.xy += position.xy * aHalo.w * (0.94 + 0.06 * flick);
  gl_Position = projectionMatrix * mvPosition;
  #include <fog_vertex>
}`;

const HALO_FRAG = /* glsl */ `
varying vec2 vUv;
varying vec3 vColor;
#include <fog_pars_fragment>
void main() {
  float r = length(vUv * 2.0 - 1.0);
  float a = pow(max(0.0, 1.0 - r), 2.2);
  if (a < 0.003) discard;
  gl_FragColor = vec4(vColor * a, 1.0);
  #ifdef USE_FOG
    #ifdef FOG_EXP2
      float fogFactor = 1.0 - exp(-fogDensity * fogDensity * vFogDepth * vFogDepth);
    #else
      float fogFactor = smoothstep(fogNear, fogFar, vFogDepth);
    #endif
    gl_FragColor.rgb *= 1.0 - fogFactor;
  #endif
}`;

const FLAME_A = new THREE.Color(3.2, 1.5, 0.45);
const FLAME_B = new THREE.Color(1.1, 0.18, 0.04);
const EMBER_A = new THREE.Color(3.5, 1.4, 0.3);
const EMBER_B = new THREE.Color(0.9, 0.2, 0.05);

interface PoolLight {
  light: THREE.PointLight;
  src: LightSource | null;
  next: LightSource | null;
  level: number;
}

export interface PropLightOptions {
  /** Zone multiplier for pools/halos/point lights (dark dungeons > 1). */
  fireScale: number;
  /** 0 (bright day) .. 1 (dark interior): scales ground pools. */
  darkness: number;
}

/**
 * Light and fire sockets of a zone:
 *
 * - every socket gets an additive light pool draped on the ground and a
 *   flickering halo (one draw call each for the whole zone);
 * - a fixed pool of real point lights (quality preset: 0/2/4; constant count,
 *   so no shader recompiles) follows the sockets nearest the player, fading
 *   in and out as they are reassigned;
 * - fire sockets near the player emit flame and ember particles.
 */
export class PropLights {
  readonly group = new THREE.Group();
  private sources: LightSource[];
  private dynamic = new Map<string, LightSource>();
  private pools: THREE.Mesh | null = null;
  private halos: THREE.Mesh | null = null;
  private haloMat: THREE.ShaderMaterial | null = null;
  private lights: PoolLight[] = [];
  private time = 0;
  private assignT = 0;
  private emitAcc = 0;
  private dirty = true;
  private nearFires: LightSource[] = [];
  private combined: LightSource[] | null = null;

  constructor(
    sources: LightSource[],
    private opts: PropLightOptions,
    private particles: ParticleSystem | null,
    private heightAt: (x: number, z: number) => number,
  ) {
    this.sources = sources;
    this.group.name = 'prop_lights';
  }

  /** Adds or replaces a dynamic source (portals, markers). */
  setDynamic(id: string, src: LightSource | null): void {
    if (src) this.dynamic.set(id, src);
    else this.dynamic.delete(id);
    this.dirty = true;
    this.combined = null;
  }

  private all(): LightSource[] {
    this.combined ??= this.dynamic.size ? [...this.sources, ...this.dynamic.values()] : this.sources;
    return this.combined;
  }

  /** Rebuilds the instanced pools and halos (sources changed). */
  private rebuild(): void {
    this.dirty = false;
    if (this.pools) {
      this.group.remove(this.pools);
      this.pools.geometry.dispose();
      (this.pools.material as THREE.Material).dispose();
      this.pools = null;
    }
    if (this.halos) {
      this.group.remove(this.halos);
      this.halos.geometry.dispose();
      this.haloMat?.dispose();
      this.halos = null;
    }
    const list = this.all();
    if (list.length === 0) return;
    const { fireScale, darkness } = this.opts;
    // Ground pools: one merged mesh of small grids draped over the terrain (static sockets).
    const G = 6;
    const per = (G + 1) * (G + 1);
    const pos = new Float32Array(list.length * per * 3);
    const uv = new Float32Array(list.length * per * 2);
    const col = new Float32Array(list.length * per * 3);
    const idx: number[] = [];
    const c = new THREE.Color();
    list.forEach((s, i) => {
      const r = s.distance * (s.kind === 'fire' ? 0.75 : 0.6);
      const h = s.pos.y - s.ground;
      // Taller sockets throw a wider, fainter pool.
      const k = (s.intensity / 4) * fireScale * (0.04 + 0.16 * darkness) * Math.min(1, 2.2 / Math.max(0.8, h));
      c.copy(s.color).multiplyScalar(k);
      const base = i * per;
      for (let gz = 0; gz <= G; gz++) {
        for (let gx = 0; gx <= G; gx++) {
          const v = base + gz * (G + 1) + gx;
          const u = gx / G;
          const w = gz / G;
          const x = s.pos.x + (u * 2 - 1) * r;
          const z = s.pos.z + (w * 2 - 1) * r;
          pos[v * 3] = x;
          pos[v * 3 + 1] = this.heightAt(x, z) + 0.07;
          pos[v * 3 + 2] = z;
          uv[v * 2] = u;
          uv[v * 2 + 1] = w;
          col[v * 3] = c.r;
          col[v * 3 + 1] = c.g;
          col[v * 3 + 2] = c.b;
        }
      }
      for (let gz = 0; gz < G; gz++) {
        for (let gx = 0; gx < G; gx++) {
          const a = base + gz * (G + 1) + gx;
          const b = a + 1;
          const d = a + G + 1;
          idx.push(a, d, b, b, d, d + 1);
        }
      }
    });
    const poolGeo = new THREE.BufferGeometry();
    poolGeo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    poolGeo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
    poolGeo.setAttribute('color', new THREE.BufferAttribute(col, 3));
    poolGeo.setIndex(idx);
    poolGeo.computeBoundingSphere();
    // Additive: fog must not tint it (it would brighten the quad), so fog is off.
    const poolMat = new THREE.MeshBasicMaterial({ map: poolTexture(), vertexColors: true, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, fog: false, toneMapped: false, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1 });
    const pools = new THREE.Mesh(poolGeo, poolMat);
    pools.renderOrder = 2;
    pools.name = 'light_pools';
    this.pools = pools;
    this.group.add(pools);
    // Halos.
    const base = new THREE.PlaneGeometry(1, 1);
    const geo = new THREE.InstancedBufferGeometry();
    geo.index = base.index;
    geo.setAttribute('position', base.getAttribute('position'));
    geo.setAttribute('uv', base.getAttribute('uv'));
    const halo = new Float32Array(list.length * 4);
    const hcol = new Float32Array(list.length * 4);
    list.forEach((s, i) => {
      halo.set([s.pos.x, s.pos.y + (s.kind === 'fire' ? 0.2 : 0), s.pos.z, s.kind === 'fire' ? 1.05 : 0.75], i * 4);
      const k = (s.intensity / 4) * (s.kind === 'fire' ? 0.9 : 0.8) * Math.max(0.6, fireScale * 0.7);
      hcol.set([s.color.r * k, s.color.g * k, s.color.b * k, Math.random() * 10], i * 4);
    });
    geo.setAttribute('aHalo', new THREE.InstancedBufferAttribute(halo, 4));
    geo.setAttribute('aColor', new THREE.InstancedBufferAttribute(hcol, 4));
    geo.instanceCount = list.length;
    this.haloMat = new THREE.ShaderMaterial({
      vertexShader: HALO_VERT,
      fragmentShader: HALO_FRAG,
      uniforms: { ...THREE.UniformsUtils.clone(THREE.UniformsLib.fog), uTime: { value: 0 } },
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      fog: true,
    });
    const halos = new THREE.Mesh(geo, this.haloMat);
    halos.frustumCulled = false;
    halos.renderOrder = 13;
    halos.name = 'light_halos';
    this.halos = halos;
    this.group.add(halos);
  }

  /** Keeps the point-light pool at `count` lights (quality change). */
  private ensurePool(count: number): void {
    while (this.lights.length > count) {
      const l = this.lights.pop() as PoolLight;
      this.group.remove(l.light);
      l.light.dispose();
    }
    while (this.lights.length < count) {
      const light = new THREE.PointLight(0xffffff, 0, 8, 2);
      light.castShadow = false;
      this.group.add(light);
      this.lights.push({ light, src: null, next: null, level: 0 });
    }
  }

  update(dt: number, focus: THREE.Vector3, pointLights: number, density: number): void {
    this.time += dt;
    if (this.dirty) this.rebuild();
    if (this.haloMat) (this.haloMat.uniforms.uTime as THREE.IUniform<number>).value = this.time;
    this.ensurePool(pointLights);
    const list = this.all();
    this.assignT -= dt;
    if (this.assignT <= 0) {
      this.assignT = 0.3;
      const near = list
        .map((s) => ({ s, d: Math.hypot(s.pos.x - focus.x, s.pos.z - focus.z) }))
        .filter((e) => e.d < 38)
        .sort((a, b) => a.d - b.d);
      this.nearFires = near.filter((e) => e.s.kind === 'fire' && e.d < 32).slice(0, 18).map((e) => e.s);
      const want = near.slice(0, this.lights.length).map((e) => e.s);
      // Keep lights that are still wanted; hand the others over.
      const free: PoolLight[] = [];
      for (const pl of this.lights) {
        const cur = pl.next ?? pl.src;
        const i = cur ? want.indexOf(cur) : -1;
        if (i >= 0) want.splice(i, 1);
        else free.push(pl);
      }
      for (const pl of free) pl.next = want.shift() ?? null;
    }
    const { fireScale } = this.opts;
    for (const pl of this.lights) {
      // Fade out before moving to another socket, then fade in.
      if (pl.next !== null && pl.next !== pl.src) {
        pl.level = Math.max(0, pl.level - dt * 4);
        if (pl.level <= 0) {
          pl.src = pl.next;
          pl.next = null;
        }
      } else if (pl.next === null && pl.src && !list.includes(pl.src)) {
        pl.level = Math.max(0, pl.level - dt * 4);
        if (pl.level <= 0) pl.src = null;
      } else if (pl.src) {
        pl.next = null;
        pl.level = Math.min(1, pl.level + dt * 2.5);
      }
      const s = pl.src;
      const l = pl.light;
      // Idle lights stay in the scene at zero intensity: toggling visibility would change the
      // lights hash and make every material re-resolve (or recompile) its program.
      if (!s || pl.level <= 0) {
        l.intensity = 0;
        continue;
      }
      const flick = s.kind === 'fire' ? 0.85 + 0.1 * Math.sin(this.time * 11 + s.pos.x) + 0.05 * Math.sin(this.time * 27 + s.pos.z) : 1;
      l.position.copy(s.pos);
      l.color.copy(s.color);
      l.distance = s.distance * 1.35;
      l.intensity = s.intensity * 2.6 * fireScale * flick * pl.level;
    }
    this.emitFire(dt, density);
  }

  private emitFire(dt: number, density: number): void {
    const ps = this.particles;
    if (!ps || this.nearFires.length === 0 || density <= 0) return;
    this.emitAcc += dt * 16 * density;
    const n = Math.floor(this.emitAcc);
    if (n <= 0) return;
    this.emitAcc -= n;
    const fires = this.nearFires;
    for (let k = 0; k < n; k++) {
      for (const f of fires) {
        const flame = Math.random() < 0.82;
        const jitter = f.distance > 5 ? 0.22 : 0.14;
        ps.spawn({
          x: f.pos.x + (Math.random() - 0.5) * jitter,
          y: f.pos.y - 0.12 + Math.random() * 0.1,
          z: f.pos.z + (Math.random() - 0.5) * jitter,
          vx: (Math.random() - 0.5) * (flame ? 0.25 : 0.9),
          vy: flame ? 0.9 + Math.random() * 0.8 : 1.6 + Math.random() * 1.6,
          vz: (Math.random() - 0.5) * (flame ? 0.25 : 0.9),
          color: flame ? FLAME_A : EMBER_A,
          colorEnd: flame ? FLAME_B : EMBER_B,
          size: flame ? 0.34 + Math.random() * 0.18 : 0.05,
          sizeEnd: flame ? 0.06 : 0.02,
          life: flame ? 0.45 + Math.random() * 0.3 : 0.9 + Math.random() * 0.8,
          drag: flame ? 1.2 : 0.6,
          gravity: flame ? -0.6 : -0.2,
          alpha: flame ? 0.75 : 1,
        });
      }
    }
  }

  dispose(): void {
    for (const pl of this.lights) pl.light.dispose();
    this.lights = [];
    this.pools?.geometry.dispose();
    if (this.pools) (this.pools.material as THREE.Material).dispose();
    this.halos?.geometry.dispose();
    this.haloMat?.dispose();
    this.group.clear();
  }
}
